import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import LoadingSkeleton from "@/components/shared/LoadingSkeleton";
import ErrorState from "@/components/shared/ErrorState";
import EmptyState from "@/components/shared/EmptyState";
import { useAreaTreeInventory, useAreasPiloto } from "@/hooks";
import { hasPermission } from "@/lib/auth";
import type { QuadraTreeInventory } from "@/types/tree-inventory";
import PreliminaryDataBanner from "../inspecoes/components/PreliminaryDataBanner";
import TreeStatisticsSection from "./components/TreeStatisticsSection";

type Tab = "estatisticas" | "quadras";

type Filter = "todas" | "com_estimativa" | "sem_cadastro" | "com_cadastro";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "todas", label: "Todas" },
  { id: "com_estimativa", label: "Com estimativa" },
  { id: "sem_cadastro", label: "Sem exemplar cadastrado" },
  { id: "com_cadastro", label: "Com exemplar cadastrado" },
];

const RISK_LABELS: Record<string, { label: string; className: string }> = {
  BAIXO: { label: "Baixo", className: "bg-primary-50 text-primary-700" },
  MEDIO: { label: "Médio", className: "bg-warning-50 text-warning-700" },
  ALTO: { label: "Alto", className: "bg-danger-50 text-danger-600" },
  CRITICO: { label: "Crítico", className: "bg-danger-100 text-danger-700" },
};

const STATUS_LABELS: Record<string, string> = {
  ATIVO: "Ativo",
  CAIDA: "Caído",
  MORTA: "Morto",
  SUPRIMIDA: "Suprimido",
  DESAPARECIDA: "Desaparecido",
};

function formatDate(value: string | null): string {
  if (!value) return "—";
  // Data sem hora: new Date() a leria como meia-noite UTC e mostraria o dia anterior.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) return `${day[3]}/${day[2]}/${day[1]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("pt-BR");
}

function Progress({ registered, estimated }: { registered: number; estimated: number | null }) {
  if (!estimated) return <span className="text-[11px] text-foreground-400">Sem estimativa</span>;
  const pct = Math.round((registered / estimated) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-background-100 overflow-hidden">
        <div className="h-full bg-primary-500" style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-[11px] text-foreground-600 w-10 text-right">{pct}%</span>
    </div>
  );
}

export default function TerritorioArvoresPage() {
  const navigate = useNavigate();
  const { areasPiloto, loading: areasLoading } = useAreasPiloto();
  const [areaId, setAreaId] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>("estatisticas");
  const [filter, setFilter] = useState<Filter>("todas");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const canAddTree = hasPermission("gis.write") && (hasPermission("tree.create") || hasPermission("cta.write"));

  useEffect(() => {
    if (areaId !== null || areasPiloto.length === 0) return;
    const real = areasPiloto.find((a) => a.code === "SBV-REAL-001") ?? areasPiloto[0];
    setAreaId(real.id);
  }, [areasPiloto, areaId]);

  const { data: inventory, loading, error } = useAreaTreeInventory(areaId);

  const quadras = useMemo(() => {
    if (!inventory) return [];
    const term = search.trim().toLowerCase();
    return inventory.quadras.filter((q) => {
      if (filter === "com_estimativa" && q.estimatedTreeCount === null) return false;
      if (filter === "sem_cadastro" && q.registeredCount > 0) return false;
      if (filter === "com_cadastro" && q.registeredCount === 0) return false;
      if (!term) return true;
      return (
        q.quadraCode.toLowerCase().includes(term) ||
        q.trees.some((t) => t.ctaCode.toLowerCase().includes(term) || (t.speciesPopularName ?? "").toLowerCase().includes(term))
      );
    });
  }, [inventory, filter, search]);

  const openConsole = (q: QuadraTreeInventory) => {
    const params = new URLSearchParams({ modo: "exemplar", quadra: String(q.quadraId) });
    if (areaId) params.set("area", String(areaId));
    navigate(`/territorio/console-webgis?${params.toString()}`);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Território > Árvores"
        subtitle="Inventário arbóreo: estatísticas dos exemplares cadastrados no CTA e, por quadra, a estimativa das inspeções territoriais (FITA)."
      />

      <div className="flex gap-1 border-b border-background-200">
        {([
          { id: "estatisticas", label: "Estatísticas", icon: "ri-bar-chart-2-line" },
          { id: "quadras", label: "Por quadra (FITA × CTA)", icon: "ri-layout-grid-line" },
        ] as { id: Tab; label: string; icon: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-sm px-4 py-2 -mb-px border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
              tab === t.id
                ? "border-primary-500 text-primary-700 font-medium"
                : "border-transparent text-foreground-500 hover:text-foreground-800"
            }`}
          >
            <i className={`${t.icon} mr-1.5`} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "estatisticas" && <TreeStatisticsSection />}

      {tab === "quadras" && (
      <>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] text-foreground-500 block mb-1">Área piloto</label>
          <select
            value={areaId ?? ""}
            onChange={(e) => {
              setAreaId(Number(e.target.value) || null);
              setExpanded(null);
            }}
            disabled={areasLoading}
            className="text-sm border border-background-200 rounded-md px-3 py-2 bg-white min-w-[260px]"
          >
            {areasPiloto.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.code}){a.code === "SBV-DEMO-001" ? " — demo" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] text-foreground-500 block mb-1">Buscar</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Quadra, código CTA ou espécie"
            className="w-full text-sm border border-background-200 rounded-md px-3 py-2 bg-white"
          />
        </div>
      </div>

      {loading && <LoadingSkeleton type="table" count={6} />}
      {!loading && error && <ErrorState message="Não foi possível carregar o inventário por quadra." />}

      {!loading && !error && inventory && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard label="Árvores estimadas (FITA)" value={inventory.estimatedTotal} icon="ri-eye-line" variant="info" />
            <KpiCard label="Exemplares cadastrados (CTA)" value={inventory.registeredTotal} icon="ri-plant-line" variant="success" />
            <KpiCard label="Com vistoria validada" value={inventory.validatedTotal} icon="ri-shield-check-line" />
            <KpiCard label="Risco alto ou crítico" value={inventory.highRiskTotal} icon="ri-alert-line" variant="danger" />
            <KpiCard
              label="Quadras com cadastro"
              value={`${inventory.quadrasWithRegistered} de ${inventory.quadrasTotal}`}
              icon="ri-layout-grid-line"
            />
          </div>

          <PreliminaryDataBanner />

          <p className="text-[11px] text-foreground-500">
            A estimativa vem da análise visual preliminar de cada quadra e serve para planejar o levantamento, não para
            substituí-lo. Um exemplar é contado na quadra mais próxima a até {inventory.maxDistanceM} m, para incluir os
            de calçada. Risco considera só vistorias validadas.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
                  filter === f.id
                    ? "bg-primary-500 text-white border-primary-500"
                    : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {inventory.quadras.length === 0 ? (
            <EmptyState
              icon="ri-layout-grid-line"
              title="Nenhuma quadra operacional nesta área piloto"
              description="As quadras são promovidas a partir da base cadastral. Sem quadras, não há como distribuir os exemplares."
            />
          ) : quadras.length === 0 ? (
            <p className="text-sm text-foreground-500 py-6 text-center">Nenhuma quadra corresponde ao filtro.</p>
          ) : (
            <div className="bg-white border border-background-200/70 rounded-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-50 text-[11px] uppercase tracking-wide text-foreground-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Quadra</th>
                    <th className="text-right px-3 py-2.5">Estimadas</th>
                    <th className="text-right px-3 py-2.5">Cadastradas</th>
                    <th className="text-left px-3 py-2.5">Andamento</th>
                    <th className="text-right px-3 py-2.5">Validadas</th>
                    <th className="text-right px-3 py-2.5">Risco alto</th>
                    <th className="text-right px-4 py-2.5">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {quadras.map((q) => {
                    const isOpen = expanded === q.quadraId;
                    return (
                      <Fragment key={q.quadraId}>
                        <tr className="border-t border-background-100 hover:bg-background-50/60">
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-foreground-900">{q.quadraCode}</div>
                            <div className="text-[11px] text-foreground-400">
                              {q.inspectionCode ? `${q.inspectionCode} · ${formatDate(q.inspectionDate)}` : "Sem inspeção territorial"}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-foreground-700">{q.estimatedTreeCount ?? "—"}</td>
                          <td className="px-3 py-2.5 text-right font-medium text-foreground-900">{q.registeredCount}</td>
                          <td className="px-3 py-2.5">
                            <Progress registered={q.registeredCount} estimated={q.estimatedTreeCount} />
                          </td>
                          <td className="px-3 py-2.5 text-right text-foreground-700">{q.validatedCount}</td>
                          <td className={`px-3 py-2.5 text-right ${q.highRiskCount > 0 ? "text-danger-600 font-medium" : "text-foreground-700"}`}>
                            {q.highRiskCount}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-1.5">
                              <button
                                onClick={() => setExpanded(isOpen ? null : q.quadraId)}
                                disabled={q.registeredCount === 0}
                                className="text-xs px-2.5 py-1.5 rounded-md border border-background-200 text-foreground-700 hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                              >
                                {isOpen ? "Ocultar" : "Exemplares"}
                              </button>
                              {canAddTree && (
                                <button
                                  onClick={() => openConsole(q)}
                                  title="Abrir o Console de Edição nesta quadra para registrar exemplares"
                                  className="text-xs px-2.5 py-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                                >
                                  <i className="ri-add-line" /> Adicionar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-background-50/60">
                            <td colSpan={7} className="px-4 py-3">
                              <ul className="divide-y divide-background-100">
                                {q.trees.map((t) => {
                                  const risk = t.latestValidatedRiskClass ? RISK_LABELS[t.latestValidatedRiskClass] : null;
                                  return (
                                    <li key={t.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                      <Link to={`/cta/${t.id}`} className="font-medium text-primary-600 hover:underline w-32">
                                        {t.ctaCode}
                                      </Link>
                                      <span className="flex-1 min-w-[160px] text-foreground-700">
                                        {t.speciesPopularName ?? "Espécie não identificada"}
                                        {t.speciesScientificName && <i className="text-foreground-400"> · {t.speciesScientificName}</i>}
                                      </span>
                                      <span className="text-foreground-500">{STATUS_LABELS[t.status] ?? t.status}</span>
                                      <span className="text-foreground-500">
                                        {t.insideQuadra ? "Dentro da quadra" : `Calçada, a ${t.distanceM.toLocaleString("pt-BR")} m`}
                                      </span>
                                      {risk ? (
                                        <span className={`px-2 py-0.5 rounded-full ${risk.className}`}>Risco {risk.label.toLowerCase()}</span>
                                      ) : (
                                        <span className="text-foreground-400">Sem vistoria validada</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}
